"use client";

import React, { Suspense, useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Plus, X, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import HeaderBefore from "@/app/components/headerBefore";
import HeaderAfter from "@/app/components/headerAfter";
import FooterBefore from '@/app/components/footerBefore';
import FooterAfter from '@/app/components/footerAfter';
import LoadingSpinner from '@/app/components/loadingSpinner';
import Breadcrumb from '@/app/components/breadcrumb';

// --- FAQ DATA ---
const faqData = [
  {
    question: 'How do I reset my password?',
    answer: 'Click on the "Forgot Password" link on the login page. Enter your registered email address, and we will send you a link to reset your password. The link will expire in 24 hours for security reasons.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We currently accept payment via bank transfer. After you place your order, our back office will issue an official invoice with payment instructions.',
  },
  {
    question: 'Is there a free trial available?',
    answer: 'We do not offer a free trial at this time. However, we encourage you to review our bundle features or schedule a demo with our team to see if the platform meets your needs.',
  },
  {
    question: 'How do I upgrade or change my bundle?',
    answer: 'Upgrade or change your bundle via your account dashboard. For details on eligibility and prorating, refer to your bundle\'s Terms & Conditions.',
  },
  {
    question: 'How is my data stored and protected?',
    answer: 'Your data is stored on secure, encrypted servers. We implement industry-standard security measures, including firewalls and regular security audits, to protect your information from unauthorized access.',
  },
  {
    question: 'What happens after I cancel my subscription?',
    answer: 'After cancellation, you will retain access to your account and data until the end of your current billing period. Once the subscription expires, your account will be downgraded, and access to paid features will be discontinued.',
  },
  {
    question: 'Do you offer training for new users?',
    answer: 'Yes! We provide comprehensive training resources, including video tutorials, user guides, and live webinar sessions. You can access these materials anytime from the "Help" or "Resources" section within your portal.',
  },
  {
    question: 'How can I contact customer support?',
    answer: (
      <>
        You can reach our customer support team by emailing{' '}
        <a
          href="hello@sdemarketplace.my"
          className="text-indigo-700 hover:underline"
        >
          hello@sdemarketplace.my
        </a>{' '}
        or using the live chat feature within the portal. Our team aims to respond to all inquiries within one business day.
      </>
    ),
  },
  {
    question: 'Are there any hidden fees?',
    answer: 'No, there are no hidden fees. The price displayed for your selected bundle is the amount you will be charged annually, plus any applicable taxes (e.g., SST).',
  },
  {
    question: 'Can I request a custom bundle?',
    answer: 'Yes, we understand that every business is unique. Please contact our support team with your requirements, and we will work with you to create a tailored solution.',
  },
  {
    question: 'Can I access the portal on my mobile device?',
    answer: 'Yes, the portal is mobile-responsive and can be accessed through any modern web browser on your smartphone or tablet.',
  },
  {
    question: 'Is SST (Sales & Service Tax) included in the price?',
    answer: 'No, SST is not included in the displayed bundle prices. It will be calculated and clearly itemized during the checkout process before you confirm your payment.',
  },
];

interface Faq {
  question: string;
  answer: React.ReactNode;
}

// This makes the main component cleaner and handles its own logic.
const FaqItem = ({ faq, isOpen, onClick }: { faq: { question: string, answer: React.ReactNode }, isOpen: boolean, onClick: () => void }) => {
  return (
    <div
      className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
    >
      {/* Question and Icon Container */}
      <div className="p-6 flex justify-between items-start cursor-pointer"
        onClick={onClick}
      >
        <h3 className="font-semibold text-slate-800 text-base md:text-xl pr-4">
          {faq.question}
        </h3>
        <div className="relative mt-1 flex-shrink-0 h-5 w-5">
          {/* Plus Icon: Rotates and fades out */}
          <Plus
            size={20}
            className={`text-slate-500 absolute transition-all duration-300 ease-in-out ${isOpen ? 'rotate-90 opacity-0' : 'rotate-0 opacity-100'
              }`}
          />

          {/* X (Cancel) Icon: Rotates in and fades in */}
          <X
            size={20}
            className={`text-slate-500 absolute transition-all duration-300 ease-in-out ${isOpen ? 'rotate-0 opacity-100' : '-rotate-90 opacity-0'
              }`}
          />
        </div>
      </div>

      {/* Answer Container with Height and Fade Animation */}
      <div
        className={`transition-[max-height] duration-500 ease-in-out ${isOpen ? 'max-h-[1000px]' : 'max-h-0'
          }`}
      >
        {/* Inner wrapper for padding and opacity transition */}
        <div className={`px-6 pb-6 text-slate-600 text-sm md:text-base transition-opacity duration-500 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {faq.answer}
        </div>
      </div>
    </div>
  );
};

function SupportPageContent() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { data: session, status } = useSession();
  const searchParams = useSearchParams();
  const [faqList, setFaqList] = useState<Faq[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  // for page loading
  const [error, setError] = useState<string | null>(null);
  // for form submission
  const [errors, setErrors] = useState({
    fullName: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fieldOrder: (keyof typeof errors)[] = ['fullName', 'email', 'message'];
    const errorFieldIdMap = {
      fullName: 'full-name',
      email: 'email',
      message: 'message',
    };
    const firstErrorFieldKey = fieldOrder.find(field => errors[field]);
    if (firstErrorFieldKey) {
      const elementId = errorFieldIdMap[firstErrorFieldKey];
      if (elementId) {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
    }
  }, [errors]);

  useEffect(() => {
    const fetchSupportData = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        setFaqList(faqData);
      } catch (err: unknown) {
        let errorMessage = 'An unexpected error occurred.';
        if (err instanceof Error) {
          errorMessage = err.message;
        }
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }

      if (status === 'authenticated' && session?.user?.email) {
        try {
          const response = await fetch('/api/user/details', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: session.user.email }),
          });

          if (response.ok) {
            const data = await response.json();
            // Pre-fill form state
            setFullName(data.fullName || '');
            setCompanyName(data.companyName || '');
            setEmail(session.user.email);
          }
        } catch (fetchError) {
          console.error("Could not fetch user details:", fetchError);
        }
      }
    };

    fetchSupportData();
  }, [status, session]);

  // useEffect to handle autofill support msg
  useEffect(() => {
    const trainingName = searchParams.get('training');
    const serviceName = searchParams.get('service');
    const inquiry = searchParams.get('inquiry');

    if (trainingName) {
      const autoFillMessage = `Hi, I would like to learn more about ${trainingName}!`;
      setMessage(autoFillMessage);
    } else if (serviceName) {
      const autoFillMessage = `Hi, I would like to learn more about the ${serviceName} service!`;
      setMessage(autoFillMessage);
    } else if (inquiry === 'bundles') {
      const autoFillMessage = `Hi, I would like to learn more about the bundles!`;
      setMessage(autoFillMessage);
    }
  }, [searchParams]);

  // useEffect to handle scrolling after page load 
  useEffect(() => {
    if (!isLoading) {
      if (window.location.hash === '#contact-us') {
        const element = document.getElementById('contact-us');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [isLoading]);

  // validation logic
  const validateField = (name: string, value: string) => {
    let error = '';
    switch (name) {
      case 'fullName':
        if (!value) {
          error = 'Full name is required.';
        } else if (!/^[A-Z\s.'-/@]+$/.test(value)) {
          error = "Full name can only contain letters, spaces, and characters .'-/@.";
        } else if (value.trim().length < 3) {
          error = "Full name must be at least 3 characters long.";
        }
        break;
      case 'email':
        if (!value) {
          error = 'Email is required.';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          error = 'Please enter a valid email address.';
        }
        break;
      case 'message':
        if (!value) error = 'Message is required.';
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newErrors = {
      fullName: validateField('fullName', fullName),
      email: validateField('email', email),
      message: validateField('message', message),
    };

    setErrors(newErrors);
    const isValid = Object.values(newErrors).every(error => error === '');

    if (isValid) {
      setIsSubmitting(true);
      const toastId = toast.loading('Sending your email...', {
        duration: Infinity,
      });

      try {
        const response = await fetch('/api/support', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fullName, companyName, email, message }),
        });

        if (response.ok) {
          toast.success('Thank you! Your ticket has been submitted.', {
            id: toastId,
            duration: 2000
          });

          // Reset form fields
          setMessage('');
          // Only clear user details if they are not logged in
          if (status !== 'authenticated') {
            setFullName('');
            setCompanyName('');
            setEmail('');
          }
        } else {
          // Handle server-side errors
          const data = await response.json();
          toast.error(data.error || 'Failed to send message. Please try again.', {
            id: toastId,
            duration: 3000
          });
        }
      } catch (error) {
        console.error('Support form submission error:', error);
        toast.error('An unexpected error occurred. Please check your connection and try again.', {
          id: toastId,
          duration: 3000
        });
      } finally {
        setIsSubmitting(false);
      }

    } else {
      console.log('Form has errors.');
    }
  };

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const middleIndex = Math.ceil(faqList.length / 2);
  const leftColumnFaqs = faqList.slice(0, middleIndex);
  const rightColumnFaqs = faqList.slice(middleIndex);
  const homePath = status === 'authenticated' ? '/dashboard' : '/';

  // Loading and Error UI States
  // This internal loading state will be shown inside the Suspense fallback
  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className='flex justify-center items-center h-screen'>
        <p className='text-xl text-red-600'>Error: {error}</p>
      </div>
    );
  }

  return (
    <>
      {status === 'authenticated' ? <HeaderAfter /> : <HeaderBefore />}

      <section className="bg-slate-50 py-10 md:py-24">
        <div className="container mx-auto px-4">

          <Breadcrumb
            homePath={homePath}
            currentPage="Support"
            className="-mt-19 ml-26 mb-8"
          />

          <div className="max-w-3xl mx-auto text-center mb-8 md:mb-12">
            <h1 className="text-2xl md:text-4xl font-bold text-slate-800">
              Frequently Asked Questions
            </h1>
            <p className="mt-2 md:mt-4 text-base md:text-lg text-slate-600">
              Answered all frequently asked questions, still confused? Feel free to open a support ticket.
            </p>
          </div>

          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-4">

            {/* --- LEFT COLUMN --- */}
            <div className="flex w-full lg:w-1/2 flex-col gap-4">
              {leftColumnFaqs.map((faq, index) => (
                <FaqItem
                  key={index}
                  faq={faq}
                  isOpen={openIndex === index}
                  onClick={() => handleToggle(index)}
                />
              ))}
            </div>

            {/* --- RIGHT COLUMN --- */}
            <div className="flex w-full lg:w-1/2 flex-col gap-4">
              {rightColumnFaqs.map((faq, index) => {
                const originalIndex = index + middleIndex;
                return (
                  <FaqItem
                    key={originalIndex}
                    faq={faq}
                    isOpen={openIndex === originalIndex}
                    onClick={() => handleToggle(originalIndex)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section id="contact-us" className="scroll-mt-30 bg-slate-50 pb-16 md:pb-24">
        <div className="container mx-auto px-4">

          {/* 1. Heading */}
          <div className="max-w-3xl mx-auto text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
              Need Help with SDeMARKETPLACE?
            </h2>
            <p className="mt-2 md:mt-4 text-base md:text-lg text-slate-600">
              Get personalized assistance, submit a support ticket. Our team will be in touch ASAP.
            </p>
          </div>

          {/* 2. Form Card */}
          <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-lg p-8">
            <form onSubmit={handleSubmit} method="POST" noValidate>
              <div className="space-y-5 md:space-y-6">

                {/* Row 1: Full Name & Company ID */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="full-name" className="block text-sm md:text-base font-semibold text-slate-700 mb-1 md:mb-2">
                      Full Name<span className="text-red-500">*</span>
                    </label>
                    <input
                      suppressHydrationWarning
                      type="text"
                      name="fullName"
                      id="full-name"
                      placeholder="Enter your full name"
                      className={`w-full px-4 py-2 md:py-3 bg-white rounded-lg border focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 text-sm md:text-base ${errors.fullName ? 'border-red-500' : 'border-gray-200'}`}
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value.toUpperCase())}
                      onBlur={handleBlur}
                    />
                    {errors.fullName && <p className="text-red-600 text-xs md:text-sm mt-1">{errors.fullName}</p>}
                  </div>
                  <div>
                    <label htmlFor="company-name" className="block text-sm md:text-base font-semibold text-slate-700 mb-1 md:mb-2">
                      Company Name
                    </label>
                    <input
                      suppressHydrationWarning
                      type="text"
                      name="companyName"
                      id="company-name"
                      placeholder="Enter your company name"
                      className="w-full px-4 py-2 md:py-3 bg-white rounded-lg border border-gray-200 focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 text-sm md:text-base"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                    />
                  </div>
                </div>

                {/* Row 2: Email */}
                <div>
                  <label htmlFor="email" className="block text-sm md:text-base font-semibold text-slate-700 mb-1 md:mb-2">
                    Email<span className="text-red-500">*</span>
                  </label>
                  <input
                    suppressHydrationWarning
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Enter your email"
                    className={`w-full px-4 py-2 md:py-3 bg-white rounded-lg border focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 text-sm md:text-base ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={handleBlur}
                  />
                  {errors.email && <p className="text-red-600 text-xs md:text-sm mt-1">{errors.email}</p>}
                </div>

                {/* Row 3: Message */}
                <div>
                  <label htmlFor="message" className="block text-sm md:text-base font-semibold text-slate-700 mb-1 md:mb-2">
                    Message<span className="text-red-500">*</span>
                  </label>
                  <textarea
                    suppressHydrationWarning
                    name="message"
                    id="message"
                    rows={6}
                    placeholder="Type your message"
                    className={`w-full px-4 py-2 md:py-3 bg-white rounded-lg border focus:ring-0 placeholder:text-gray-400 focus:outline-none placeholder:text-sm md:placeholder:text-base text-slate-800 text-sm md:text-base ${errors.message ? 'border-red-500' : 'border-gray-200'}`}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onBlur={handleBlur}
                  ></textarea>
                  {errors.message && <p className="text-red-600 text-xs md:text-sm">{errors.message}</p>}
                </div>

                {/* Submit Button */}
                <div>
                  <button
                    suppressHydrationWarning
                    type="submit"
                    className="w-full bg-indigo-600 text-white font-semibold py-2.5 md:py-4 px-6 rounded-lg hover:bg-indigo-700 transition-colors duration-200 disabled:bg-indigo-400 disabled:cursor-not-allowed text-sm md:text-base"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {status === 'authenticated' ? <FooterAfter /> : <FooterBefore />}
    </>
  );
}

// --- The new page component that Next.js will render ---
export default function SupportPage() {
  return (
    <Suspense>
      <SupportPageContent />
    </Suspense>
  );
}